import React, { useState, useEffect, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Target, TrendingUp, TrendingDown, Search, ChevronDown, ChevronRight,
  Info, Loader2, CheckCircle2, Circle, Lightbulb, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

/**
 * Format a metric value based on its format
 */
const formatValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  switch (format) {
    case 'currency':
      return `$${num.toLocaleString()}`;
    case 'percent':
      return `${num}%`;
    default:
      return num.toLocaleString();
  }
};

/**
 * KPI Subscription Tab - Checklist-based KPI selection
 * 
 * Shows inherited KPIs as a checklist where users can toggle subscriptions.
 * Makes it clear:
 * - What KPIs are available (inherited from parent)
 * - What KPIs are currently subscribed
 * - What children will inherit (the subscribed set)
 * 
 * Changes are passed to parent via onChange callback for unified form saving.
 */
const KpiSubscriptionTab = ({
  entityType = 'initiative', // 'initiative' | 'epic' | 'feature'
  entityId,
  disabled = false,
  fetchInheritedKpis, // async function to fetch inherited KPIs
  onChange, // callback when subscriptions change: (subscriptions) => void
  currentSubscriptions = [], // current subscribed_kpi_ids from parent form
  currentAlignments = [], // current kpi_alignments from parent form (features)
  subscriptionFieldName = 'subscribed_kpi_ids', // field name in response
  showHypothesis = false, // for features, show hypothesis input
  linkedGoalIds = [], // for initiatives: triggers refetch when goals change
  overrideData = null, // optional: bypass fetch and use this data directly (for epic change handling)
  className
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGoals, setExpandedGoals] = useState(new Set());

  // Derive subscriptions from props (controlled component pattern)
  // This ensures checkbox state always reflects parent form state
  const subscriptions = useMemo(() => {
    if (showHypothesis && currentAlignments?.length) {
      return new Set(currentAlignments.map(a => a.kpi_key));
    }
    return new Set(currentSubscriptions || []);
  }, [showHypothesis, currentAlignments, currentSubscriptions]);

  // Derive hypotheses from props (for features)
  const hypotheses = useMemo(() => {
    if (showHypothesis && currentAlignments?.length) {
      const hyps = {};
      currentAlignments.forEach(a => {
        hyps[a.kpi_key] = a.hypothesis || '';
      });
      return hyps;
    }
    return {};
  }, [showHypothesis, currentAlignments]);

  // Stringify goal IDs to use as stable dependency
  const goalIdsKey = JSON.stringify(linkedGoalIds?.slice().sort() || []);

  // Handle override data - when parent provides data directly (e.g., epic change)
  useEffect(() => {
    if (overrideData !== null) {
      setData(overrideData);
      setLoading(false);
      
      // Expand all goals by default when override data changes
      if (overrideData.by_goal) {
        setExpandedGoals(new Set(overrideData.by_goal.map(g => g.goal_id)));
      } else if (overrideData.by_source?.epic?.info) {
        setExpandedGoals(new Set(['epic']));
      }
    }
  }, [overrideData]);

  // Load inherited KPIs - refetch when entityId or linked goals change
  // Skip fetch if overrideData is provided
  useEffect(() => {
    const loadData = async () => {
      // Skip fetch if override data is provided
      if (overrideData !== null) return;
      
      if (!entityId || !fetchInheritedKpis) return;
      
      setLoading(true);
      try {
        const response = await fetchInheritedKpis(entityId);
        setData(response);
        
        // Build set of valid KPI keys from response
        const validKpiKeys = new Set(
          (response.inherited_kpis || []).map(kpi => kpi.kpi_key)
        );
        
        // Filter current subscriptions to only keep KPIs that still exist
        // and notify parent if any were removed
        const currentSubs = showHypothesis 
          ? new Set(currentAlignments?.map(a => a.kpi_key) || [])
          : new Set(currentSubscriptions || []);
        
        const filtered = new Set([...currentSubs].filter(key => validKpiKeys.has(key)));
        
        // If selections changed due to KPI removal, notify parent
        if (filtered.size !== currentSubs.size) {
          // Defer to avoid state update during render
          setTimeout(() => {
            if (showHypothesis) {
              const alignments = Array.from(filtered).map(kpiKey => ({
                kpi_key: kpiKey,
                hypothesis: hypotheses[kpiKey] || ''
              }));
              onChange?.(alignments);
            } else {
              onChange?.(Array.from(filtered));
            }
          }, 0);
        }
        
        // Expand all goals by default
        if (response.by_goal) {
          setExpandedGoals(new Set(response.by_goal.map(g => g.goal_id)));
        } else if (response.by_source?.epic?.info) {
          setExpandedGoals(new Set(['epic']));
        }
      } catch (err) {
        console.error('Failed to load inherited KPIs:', err);
        toast.error('Failed to load available KPIs');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, goalIdsKey, overrideData]);

  // Notify parent of changes
  const notifyChange = (newSubs, newHyps) => {
    if (!onChange) return;
    
    if (showHypothesis) {
      // For features, send kpi_alignments with hypotheses
      const alignments = Array.from(newSubs).map(kpiKey => ({
        kpi_key: kpiKey,
        hypothesis: newHyps[kpiKey] || ''
      }));
      onChange(alignments);
    } else {
      // For initiatives/epics, send subscribed_kpi_ids array
      onChange(Array.from(newSubs));
    }
  };

  // Toggle subscription for a KPI (controlled - updates via parent)
  const toggleSubscription = (kpiKey) => {
    if (disabled) return;
    
    // Use derived subscriptions from props
    const newSubs = new Set(subscriptions);
    let newHyps = { ...hypotheses };
    
    if (newSubs.has(kpiKey)) {
      newSubs.delete(kpiKey);
      // Also clear hypothesis if feature
      if (showHypothesis) {
        delete newHyps[kpiKey];
      }
    } else {
      newSubs.add(kpiKey);
    }
    
    // Notify parent - parent will update props, which will update our derived state
    notifyChange(newSubs, newHyps);
  };

  // Update hypothesis for a KPI (features only)
  const updateHypothesis = (kpiKey, value) => {
    const newHyps = { ...hypotheses, [kpiKey]: value };
    // Notify parent with current subscriptions and new hypotheses
    notifyChange(subscriptions, newHyps);
  };

  // Filter KPIs by search
  const filteredKpis = useMemo(() => {
    if (!data?.inherited_kpis) return [];
    if (!searchQuery) return data.inherited_kpis;
    
    const query = searchQuery.toLowerCase();
    return data.inherited_kpis.filter(kpi =>
      kpi.metric_name.toLowerCase().includes(query) ||
      kpi.goal_title.toLowerCase().includes(query)
    );
  }, [data?.inherited_kpis, searchQuery]);

  // Group KPIs by goal (or by source for features)
  const groupedKpis = useMemo(() => {
    if (!filteredKpis.length) return [];
    
    // For features, group by source (epic vs tag_initiatives)
    if (data?.by_source) {
      const groups = [];
      
      if (data.by_source.epic?.kpis?.length > 0) {
        groups.push({
          id: 'epic',
          title: `From Epic: ${data.by_source.epic.info?.title || 'Unknown'}`,
          subtitle: 'Hierarchical inheritance',
          targetDate: null,
          kpis: data.by_source.epic.kpis.filter(k => 
            filteredKpis.some(f => f.kpi_key === k.kpi_key)
          )
        });
      }
      
      if (data.by_source.tag_initiatives?.kpis?.length > 0) {
        groups.push({
          id: 'tags',
          title: 'From Tag-Following Initiatives',
          subtitle: 'Cross-cutting inheritance via shared tags',
          targetDate: null,
          kpis: data.by_source.tag_initiatives.kpis.filter(k =>
            filteredKpis.some(f => f.kpi_key === k.kpi_key)
          )
        });
      }
      
      return groups;
    }
    
    // For initiatives/epics, group by goal
    // Build a map of goal_id -> goal_target_date from by_goal data
    const goalTargetDates = {};
    if (data?.by_goal) {
      data.by_goal.forEach(g => {
        goalTargetDates[g.goal_id] = g.goal_target_date;
      });
    }
    
    const goalMap = {};
    filteredKpis.forEach(kpi => {
      const goalId = kpi.goal_id;
      if (!goalMap[goalId]) {
        goalMap[goalId] = {
          id: goalId,
          title: kpi.goal_title,
          subtitle: null,
          targetDate: goalTargetDates[goalId] || null,
          kpis: []
        };
      }
      goalMap[goalId].kpis.push(kpi);
    });
    
    return Object.values(goalMap);
  }, [filteredKpis, data?.by_source, data?.by_goal]);

  // Stats - use derived subscriptions from props
  const totalAvailable = data?.total_available || 0;
  const subscribedCount = subscriptions.size;

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-12", className)}>
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Loading available KPIs...</span>
      </div>
    );
  }

  if (!data || totalAvailable === 0) {
    return (
      <div className={cn("text-center py-12", className)}>
        <Target className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No KPIs Available
        </h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {data?.hint || data?.hint_message || getEmptyStateHint(entityType)}
        </p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-indigo-500" />
          <div>
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              {showHypothesis ? 'KPI Alignments' : 'KPI Subscriptions'}
            </h3>
            <p className="text-sm text-gray-500">
              {subscribedCount} of {totalAvailable} {showHypothesis ? 'aligned' : 'subscribed'}
              {!showHypothesis && ' • Children will inherit subscribed KPIs'}
            </p>
          </div>
        </div>
      </div>

      {/* Search (only show if multiple KPIs) */}
      {totalAvailable > 5 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search KPIs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {/* Initiative Context Breadcrumb - shows parent initiative for epics */}
      {/* This provides context that goals below belong to the same initiative */}
      {entityType === 'epic' && data?.initiative && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <Lightbulb className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-700 dark:text-amber-300">
            <span className="font-medium">Initiative:</span>{' '}
            <span>{data.initiative.title}</span>
          </span>
        </div>
      )}

      {/* KPI Groups */}
      <div className="space-y-3">
        {groupedKpis.map(group => (
          <KpiGroup
            key={group.id}
            group={group}
            expanded={expandedGoals.has(group.id)}
            onToggleExpand={() => {
              const newExpanded = new Set(expandedGoals);
              if (newExpanded.has(group.id)) {
                newExpanded.delete(group.id);
              } else {
                newExpanded.add(group.id);
              }
              setExpandedGoals(newExpanded);
            }}
            subscriptions={subscriptions}
            onToggleSubscription={toggleSubscription}
            hypotheses={hypotheses}
            onUpdateHypothesis={updateHypothesis}
            showHypothesis={showHypothesis}
            disabled={disabled}
          />
        ))}
      </div>

      {/* Info footer */}
      {!disabled && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="text-sm">
            {showHypothesis 
              ? 'Aligned KPIs explain how this feature contributes to strategic goals. Add a hypothesis for each alignment.'
              : entityType === 'initiative'
                ? 'Subscribed KPIs will be inherited by child Epics. Only subscribe to KPIs this initiative directly impacts.'
                : entityType === 'epic'
                  ? 'Subscribed KPIs will be inherited by child Features. Only subscribe to KPIs this epic directly impacts.'
                  : 'Subscribed KPIs will be inherited by children. Only subscribe to KPIs you directly impact.'
            }
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * KPI Group - Collapsible group of KPIs from same goal/source
 */
const KpiGroup = ({
  group,
  expanded,
  onToggleExpand,
  subscriptions,
  onToggleSubscription,
  hypotheses,
  onUpdateHypothesis,
  showHypothesis,
  disabled
}) => {
  const subscribedInGroup = group.kpis.filter(k => subscriptions.has(k.kpi_key)).length;
  const totalInGroup = group.kpis.length;
  
  // Format target date for display
  const formattedTargetDate = useMemo(() => {
    if (!group.targetDate) return null;
    try {
      const date = typeof group.targetDate === 'string' ? parseISO(group.targetDate) : group.targetDate;
      return format(date, 'MMM d, yyyy');
    } catch {
      return null;
    }
  }, [group.targetDate]);
  
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Group Header */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="flex items-center gap-2 flex-wrap">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {group.title}
          </span>
          {group.subtitle && (
            <span className="text-xs text-gray-500">
              ({group.subtitle})
            </span>
          )}
          {formattedTargetDate && (
            <Badge variant="outline" className="text-xs font-normal gap-1 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-600">
              <Calendar className="w-3 h-3" />
              due {formattedTargetDate}
            </Badge>
          )}
        </div>
        
        <Badge 
          variant={subscribedInGroup === totalInGroup ? 'default' : 'outline'}
          className="text-xs flex-shrink-0"
        >
          {subscribedInGroup}/{totalInGroup} {showHypothesis ? 'aligned' : 'subscribed'}
        </Badge>
      </button>
      
      {/* KPI List */}
      {expanded && (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {group.kpis.map(kpi => (
            <KpiChecklistItem
              key={kpi.kpi_key}
              kpi={kpi}
              isSubscribed={subscriptions.has(kpi.kpi_key)}
              onToggle={() => onToggleSubscription(kpi.kpi_key)}
              hypothesis={hypotheses[kpi.kpi_key]}
              onUpdateHypothesis={(value) => onUpdateHypothesis(kpi.kpi_key, value)}
              showHypothesis={showHypothesis}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Individual KPI Checklist Item
 */
const KpiChecklistItem = ({
  kpi,
  isSubscribed,
  onToggle,
  hypothesis,
  onUpdateHypothesis,
  showHypothesis,
  disabled
}) => {
  const DirectionIcon = kpi.direction === 'lower_is_better' ? TrendingDown : TrendingUp;
  const directionColor = kpi.direction === 'lower_is_better' 
    ? 'text-red-500' 
    : 'text-green-500';
  
  return (
    <div className={cn(
      "p-3 transition-colors",
      isSubscribed 
        ? "bg-indigo-50/50 dark:bg-indigo-900/20" 
        : "bg-white dark:bg-gray-900"
    )}>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div className="pt-0.5">
          <Checkbox
            checked={isSubscribed}
            onCheckedChange={onToggle}
            disabled={disabled}
            className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
          />
        </div>
        
        {/* KPI Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <DirectionIcon className={cn("w-4 h-4", directionColor)} />
            <span className={cn(
              "font-medium",
              isSubscribed 
                ? "text-gray-900 dark:text-gray-100" 
                : "text-gray-600 dark:text-gray-400"
            )}>
              {kpi.metric_name}
            </span>
          </div>
          
          {/* Value info */}
          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
            <span>
              Current: {formatValue(kpi.current_value, kpi.metric_format)}
            </span>
            <span>
              Target: {formatValue(kpi.target_value, kpi.metric_format)}
            </span>
          </div>
          
          {/* Hypothesis input for features */}
          {showHypothesis && isSubscribed && (
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Support Hypothesis
              </label>
              <Textarea
                placeholder="How will this feature impact this KPI?"
                value={hypothesis || ''}
                onChange={(e) => onUpdateHypothesis(e.target.value)}
                disabled={disabled}
                className="text-sm"
                rows={3}
              />
            </div>
          )}
        </div>
        
        {/* Status indicator */}
        <div className="pt-0.5">
          {isSubscribed ? (
            <CheckCircle2 className="w-5 h-5 text-indigo-500" />
          ) : (
            <Circle className="w-5 h-5 text-gray-300 dark:text-gray-600" />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Get empty state hint based on entity type
 */
const getEmptyStateHint = (entityType) => {
  switch (entityType) {
    case 'initiative':
      return 'Link Goals to this Initiative to inherit their KPIs. You can then subscribe to specific KPIs you want to track.';
    case 'epic':
      return 'The parent Initiative has no subscribed KPIs. Subscribe to KPIs in the Initiative first.';
    case 'feature':
      return 'No KPIs available. Either assign this feature to an Epic with subscribed KPIs, or add tags that match a tag-following Initiative.';
    default:
      return 'No KPIs available to subscribe to.';
  }
};

export default KpiSubscriptionTab;
